import { useState, useEffect } from "react";

export default function ZoomableImage({
  src,
  alt = "Image",
  thumbMaxWidth = 200,
  thumbMaxHeight = 200,
}) {
  const [open, setOpen] = useState(false);
  const [rotation, setRotation] = useState(0); // degrees

  // Close on ESC
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") close();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  function close() {
    setOpen(false);
    setRotation(0);
  }

  function rotateLeft(e) {
    e.stopPropagation();
    setRotation((r) => r - 90);
  }

  function rotateRight(e) {
    e.stopPropagation();
    setRotation((r) => r + 90);
  }

  // Normalize rotation to 0–359
  const normalized = ((rotation % 360) + 360) % 360;

  // Slight scale-down for 90° / 270° to fit viewport better
  const needsScale = normalized === 90 || normalized === 270;
  const scale = needsScale ? 0.85 : 1;

  return (
    <>
      {/* Thumbnail */}
      <img
        src={src}
        alt={alt}
        title="Double-click to enlarge"
        onDoubleClick={() => setOpen(true)}
        style={{
          maxWidth: thumbMaxWidth,
          maxHeight: thumbMaxHeight,
          objectFit: "contain",
          cursor: "zoom-in",
          border: "1px solid #ccc",
          borderRadius: 4,
        }}
      />

      {/* Modal */}
      {open && (
        <div
          onClick={close}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          {/* Modal container (NO rotating border here) */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "relative",
              maxWidth: "95vw",
              maxHeight: "95vh",
              padding: 16,
              borderRadius: 10,
              backgroundColor: "#0f0f0f",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {/* Close button */}
            <button
              onClick={close}
              title="Close"
              style={{
                position: "absolute",
                top: -14,
                right: -14,
                width: 38,
                height: 38,
                borderRadius: "50%",
                border: "2px solid #81c784",
                background: "#1b5e20",
                color: "white",
                fontSize: 20,
                fontWeight: 700,
                cursor: "pointer",
                zIndex: 5,
              }}
            >
              ✕
            </button>

            {/* -------------------------
             * Image stage
             * ------------------------- */}
            <div
              style={{
                position: "relative",
                width: "95vw",
                maxWidth: "95vw",
                height: "75vh",            // reserves stable space
                maxHeight: "75vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",        // prevents rotated overflow
              }}
            >
              {/* Rotated image + rotating border */}
              <div
                style={{
                  border: "3px solid #81c784",
                  borderRadius: 6,
                  padding: 6,
                  transform: `rotate(${rotation}deg) scale(${scale})`,
                  transformOrigin: "center center",
                  transition: "transform 0.25s ease",
                  maxWidth: "100%",
                  maxHeight: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1,
                }}
              >
                <img
                  src={src}
                  alt={alt}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    display: "block",
                    cursor: "zoom-out",
                  }}
                />
              </div>

              {/* Rotation controls overlay (always above image) */}
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: "absolute",
                  bottom: 12,
                  left: "50%",
                  transform: "translateX(-50%)",
                  display: "flex",
                  gap: 16,
                  zIndex: 3,
                  padding: "8px 12px",
                  borderRadius: 10,
                  background: "rgba(15,15,15,0.65)",
                  backdropFilter: "blur(2px)",
                }}
              >
                <button onClick={rotateLeft} style={controlButtonStyle}>
                  ⟲ Rotate Left
                </button>
                <button onClick={rotateRight} style={controlButtonStyle}>
                  Rotate Right ⟳
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* -----------------------------
 * Shared button style
 * ----------------------------- */
const controlButtonStyle = {
  padding: "10px 18px",
  fontSize: 16,
  fontWeight: 600,
  borderRadius: 8,
  border: "2px solid #81c784",
  backgroundColor: "#e8f5e9",
  color: "#1b5e20",
  cursor: "pointer",
};
